import type { TypographyVariant } from '@mui/material/styles';
import type { UpdatePolicyPeriodRequest } from 'src/api/services/policy.service';

import { useParams } from 'react-router';
import { memo, useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import {
  Box,
  Card,
  Table,
  Tooltip,
  TableRow,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  CardContent,
  InputAdornment,
  TableContainer,
} from '@mui/material';

import { usePremiumsByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';

import { EmptyContent } from 'src/components/empty-content';

import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import { StyledTooltipLabel } from '../../shared/styles/sharedStyles';
import { formatDate, formatDollarAmount } from '../../shared/utils/policyUtils';

export type PremiumHandle = {
  validateForSave: () => boolean;
  hasPendingChanges: () => boolean;
  getChangedFieldSummaries: () => string[];
  getPolicyPeriodPayload: () => UpdatePolicyPeriodRequest | null;
  commitEdit: () => void;
  cancelEdit: () => void;
};

type PremiumProps = {
  isEditMode?: boolean;
  policyPeriodId?: number;
  initialDraftDelta?: PremiumDraftDelta | null;
  onDraftDeltaChange?: (delta: PremiumDraftDelta) => void;
};

// Changed-values-only representation of the premium edit, for cross-tab-switch persistence without duplicating unedited read data.
export type PremiumDraftDelta = {
  totalPolicyPremium?: string;
};

const FIELD_LABELS: {
  key: string;
  label: string;
  variantType: TypographyVariant;
  group: number;
  textColor: string;
}[] = [
  {
    key: 'manualPremium',
    label: 'Manual premium',
    variantType: 'subtitle2',
    group: 1,
    textColor: 'text.primary',
  },
  { key: 'uslh', label: 'USL&H', variantType: 'body2', group: 1, textColor: 'text.secondary' },
  {
    key: 'totalManualPremium',
    label: 'Total manual premium',
    variantType: 'subtitle2',
    group: 2,
    textColor: 'text.primary',
  },
  {
    key: 'waiverOfSubrogationFactor',
    label: 'Waiver of subrogation factor',
    variantType: 'body2',
    group: 2,
    textColor: 'text.secondary',
  },
  {
    key: 'employersLiabilityIncreasedLimitsFactor',
    label: 'Employers Liability (E/L) increased limits factor',
    variantType: 'body2',
    group: 2,
    textColor: 'text.secondary',
  },
  {
    key: 'employersLiabilityVoluntaryCompensationFlatCharge',
    label: 'Employers Liability/Voluntary Compensation flat charge',
    variantType: 'body2',
    group: 2,
    textColor: 'text.secondary',
  },
  {
    key: 'deductibleCredit',
    label: 'Deductible credit',
    variantType: 'body2',
    group: 2,
    textColor: 'text.secondary',
  },
  {
    key: 'totalSubjectPremium',
    label: 'Total subject premium',
    variantType: 'subtitle2',
    group: 3,
    textColor: 'text.primary',
  },
  {
    key: 'experienceModification',
    label: 'Experience modification',
    variantType: 'body2',
    group: 3,
    textColor: 'text.secondary',
  },
  {
    key: 'totalModifiedPremium',
    label: 'Total modified premium',
    variantType: 'subtitle2',
    group: 4,
    textColor: 'text.primary',
  },
  {
    key: 'assignedRiskAdjustmentProgramSurcharge',
    label: 'Assigned Risk Adjustment Program (ARAP) surcharge',
    variantType: 'body2',
    group: 4,
    textColor: 'text.secondary',
  },
  {
    key: 'scheduleRatingFactor',
    label: 'Schedule Rating Factor (1-SR Credit %) or (1 + SR Debit %)',
    variantType: 'body2',
    group: 4,
    textColor: 'text.secondary',
  },
  {
    key: 'chargeForNonRatable',
    label: 'Charge for non-ratable catastrophe loading',
    variantType: 'body2',
    group: 4,
    textColor: 'text.secondary',
  },
  {
    key: 'balanceToMinimumPremiumStateAct',
    label: 'Balance to Minimum Premium (State Act)',
    variantType: 'body2',
    group: 4,
    textColor: 'text.secondary',
  },
  {
    key: 'balanceToMinimumPremiumAdmiraltyFema',
    label: 'Balance to Minimum Premium (Admiralty/FEMA)',
    variantType: 'body2',
    group: 4,
    textColor: 'text.secondary',
  },
  {
    key: 'totalStandardPremium',
    label: 'Total standard premium',
    variantType: 'subtitle2',
    group: 5,
    textColor: 'text.primary',
  },
  {
    key: 'premiumDiscount',
    label: 'Premium discount',
    variantType: 'body2',
    group: 5,
    textColor: 'text.secondary',
  },
  {
    key: 'coalMineDiseaseCharge',
    label: 'Coal mine disease charge',
    variantType: 'body2',
    group: 5,
    textColor: 'text.secondary',
  },
  {
    key: 'expenseConstant',
    label: 'Expense constant',
    variantType: 'body2',
    group: 5,
    textColor: 'text.secondary',
  },
  {
    key: 'terrorismCharge',
    label: 'Terrorism charge',
    variantType: 'body2',
    group: 5,
    textColor: 'text.secondary',
  },
  {
    key: 'catastrophe',
    label: 'Catastrophe (other than cerified acts of terrorism)',
    variantType: 'body2',
    group: 5,
    textColor: 'text.secondary',
  },
  {
    key: 'estimatedAnnualPremium',
    label: 'Estimated annual premium',
    variantType: 'subtitle2',
    group: 6,
    textColor: 'text.primary',
  },
  {
    key: 'auditNonComplianceCharge',
    label: 'Audit noncompliance charge',
    variantType: 'body2',
    group: 6,
    textColor: 'text.secondary',
  },
  {
    key: 'totalAmountDue',
    label: 'Total amount due',
    variantType: 'h6',
    group: 7,
    textColor: 'text.primary',
  },
  {
    key: 'carrierCalculatedTotalAmountDue',
    label: 'Carrier calculated total amount due',
    variantType: 'body2',
    group: 7,
    textColor: 'text.secondary',
  },
];

function PremiumTable({
  dataTestId,
  rows,
  columns,
}: {
  dataTestId: string;
  rows: any[];
  columns: string[];
}) {
  const visibleFields = getVisibleFields(rows);
  const groupedFields = groupFieldsByGroup(visibleFields);

  return (
    <TableContainer
      sx={{
        mt: 3,
      }}
    >
      <Table data-testid={dataTestId}>
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell
                sx={{
                  width: `${100 / columns.length}%`,
                  textAlign: 'right',
                  borderBottom: 'unset',
                }}
                key={index}
              >
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.values(groupedFields).map((groupedFieldsList, groupIdx, arr) => {
            const isSecondToLast = groupIdx === arr.length - 2;
            const isLast = groupIdx === arr.length - 1;
            let rowSx = undefined;
            if (!isLast) {
              rowSx = isSecondToLast
                ? { borderBottom: '1px solid', borderColor: 'text.primary' }
                : { borderBottom: '1px dashed', borderColor: 'grey.400' };
            }
            return (
              <TableRow key={groupIdx} sx={rowSx}>
                {/* Labels */}
                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right', borderBottom: 'unset' }}>
                  {groupedFieldsList.map(({ key, label, variantType, textColor }, idx) =>
                    key === 'manualPremium' ? (
                      <Tooltip
                        key={key}
                        arrow
                        title="Manual premium is the total of all class code premiums (excluding USL&H)."
                        placement="top"
                      >
                        <StyledTooltipLabel
                          data-testid="coverage-name-label"
                          color="primary"
                          sx={{
                            display: 'inline-block',
                          }}
                        >
                          <Typography
                            component="span"
                            variant={variantType}
                            color={textColor}
                            sx={{
                              display: 'block',
                              mb: idx < groupedFieldsList.length - 1 ? 1 : 0,
                            }}
                          >
                            {label}
                          </Typography>
                        </StyledTooltipLabel>
                      </Tooltip>
                    ) : (
                      <Typography
                        key={key}
                        variant={variantType}
                        color={textColor}
                        sx={{ display: 'block', mb: idx < groupedFieldsList.length - 1 ? 1 : 0 }}
                      >
                        {label}
                      </Typography>
                    )
                  )}
                </TableCell>
                {/* Values */}
                {rows.map((row, colIdx) => (
                  <TableCell
                    key={colIdx}
                    sx={{ textAlign: 'right', verticalAlign: 'top', borderBottom: 'unset' }}
                  >
                    {groupedFieldsList.map(({ key, variantType, textColor }, idx) => (
                      <Typography
                        key={key}
                        variant={variantType}
                        color={textColor}
                        sx={{ display: 'block', mb: idx < groupedFieldsList.length - 1 ? 1 : 0 }}
                        data-testid={`${key}-value`}
                      >
                        {renderCellValue(key, row[key])}
                      </Typography>
                    ))}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function getVisibleFields(rows: any[]) {
  return FIELD_LABELS.filter(({ key }) =>
    rows.some((row) => row[key] !== undefined && row[key] !== null && row[key] !== '')
  );
}

export function groupFieldsByGroup(
  visibleFields: {
    key: string;
    label: string;
    variantType: TypographyVariant;
    group: number;
    textColor: string;
  }[]
): Record<
  number,
  { key: string; label: string; variantType: TypographyVariant; group: number; textColor: string }[]
> {
  return visibleFields.reduce(
    (acc, field) => {
      acc[field.group] = acc[field.group] || [];
      acc[field.group].push(field);
      return acc;
    },
    {} as Record<
      number,
      {
        key: string;
        label: string;
        variantType: TypographyVariant;
        group: number;
        textColor: string;
      }[]
    >
  );
}

export function renderCellValue(key: string, value: number) {
  if (value == null || value === undefined) value = 0;
  if (key === 'assignedRiskAdjustmentProgramSurcharge' || key === 'experienceModification') {
    return 'x' + Number(value).toFixed(2);
  }
  return formatDollarAmount(value);
}

const Premium = forwardRef<PremiumHandle, PremiumProps>(function PremiumRenderer(
  { isEditMode = false, policyPeriodId, initialDraftDelta = null, onDraftDeltaChange },
  ref
) {
  const { comboId, coverageId, policyId } = useParams<{
    comboId: string;
    coverageId: string;
    policyId: string;
  }>();
  const { data: apiResponse, error } = usePremiumsByPolicyId(
    Number(comboId),
    Number(coverageId),
    Number(policyId)
  );

  // Determine no data state depending on if endpoint returns a 404 not found or an empty array
  // This is to handle cases where the policy exists but has no compliance history
  // or the policy does not exist at all.
  const notFound =
    error?.status === 404 || (Array.isArray(apiResponse?.data) && apiResponse.data.length === 0);

  const premiumRows = apiResponse?.data?.map((item) => ({ ...item }));
  const premiumRow = premiumRows?.[0];
  const resolvedPolicyPeriodId = policyPeriodId ?? premiumRow?.policyStatePremium?.policyPeriodId;
  const [draftTotalPolicyPremium, setDraftTotalPolicyPremium] = useState('');
  const [snapshotTotalPolicyPremium, setSnapshotTotalPolicyPremium] = useState('');
  const [fieldError, setFieldError] = useState('');
  const initialDraftDeltaRef = useRef(initialDraftDelta);
  const hasAppliedInitialDeltaRef = useRef(false);
  // Tracks the last apiResponse?.data reference actually processed, so a mount-effect
  // re-invocation with unchanged data (e.g. Strict Mode's double-invoke) is a no-op instead of
  // re-resetting the draft.
  const lastProcessedDataRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (!apiResponse?.data || lastProcessedDataRef.current === apiResponse.data) {
      return;
    }
    lastProcessedDataRef.current = apiResponse.data;
    const freshValue = String(Math.trunc(premiumRow?.totalPolicyPremium ?? 0));
    if (!hasAppliedInitialDeltaRef.current) {
      hasAppliedInitialDeltaRef.current = true;
      setDraftTotalPolicyPremium(initialDraftDeltaRef.current?.totalPolicyPremium ?? freshValue);
    } else {
      setDraftTotalPolicyPremium(freshValue);
    }
    const value = freshValue;
    setSnapshotTotalPolicyPremium(value);
    setFieldError('');
  }, [premiumRow?.totalPolicyPremium, apiResponse?.data]);

  useEffect(() => {
    if (!hasAppliedInitialDeltaRef.current || !apiResponse?.data) {
      return;
    }
    onDraftDeltaChange?.(
      draftTotalPolicyPremium === snapshotTotalPolicyPremium
        ? {}
        : { totalPolicyPremium: draftTotalPolicyPremium }
    );
  }, [draftTotalPolicyPremium, snapshotTotalPolicyPremium, onDraftDeltaChange, apiResponse?.data]);

  useImperativeHandle(ref, () => ({
    validateForSave: () => {
      const valid = /^\d{1,10}$/.test(draftTotalPolicyPremium.trim());
      setFieldError(valid ? '' : 'Must be a whole number with a maximum of 10 characters.');
      return valid;
    },
    hasPendingChanges: () => draftTotalPolicyPremium !== snapshotTotalPolicyPremium,
    getChangedFieldSummaries: () =>
      draftTotalPolicyPremium === snapshotTotalPolicyPremium
        ? []
        : [`Total policy premium changed from ${formatDollarAmount(Number(snapshotTotalPolicyPremium))} to ${formatDollarAmount(Number(draftTotalPolicyPremium))}`],
    getPolicyPeriodPayload: () =>
      draftTotalPolicyPremium === snapshotTotalPolicyPremium || !resolvedPolicyPeriodId
        ? null
        : {
            policyPeriodId: resolvedPolicyPeriodId,
            estimatedStandardPremium: Number(draftTotalPolicyPremium),
          },
    commitEdit: () => setSnapshotTotalPolicyPremium(draftTotalPolicyPremium),
    cancelEdit: () => {
      setDraftTotalPolicyPremium(snapshotTotalPolicyPremium);
      setFieldError('');
    },
  }), [draftTotalPolicyPremium, resolvedPolicyPeriodId, snapshotTotalPolicyPremium]);

  const effectiveDateListBase = [
    '',
    ...(premiumRows?.map(
      (item) =>
        formatDate(item.policyPeriodEffectiveDate) +
        ' - ' +
        formatDate(item.policyPeriodExpirationDate)
    ) || []),
  ];

  const effectiveDateList =
    effectiveDateListBase.length === 2 ? [...effectiveDateListBase, '', ''] : effectiveDateListBase;

  const headerWidth = useHeaderWidth();

  return (
    <>
      <Card
        sx={[
          () => ({
            display: 'flex',
            zIndex: 'unset',
            overflow: 'unset',
            mt: 5,
            borderRadius: 2,
            boxShadow: 'var(--customShadows-card)',
            width: headerWidth,
          }),
        ]}
      >
        <CardContent
          sx={{ flexGrow: 1, paddingTop: '1.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}
          data-testid="premiums-card"
        >
          <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="premiums-title">
            North Carolina premiums
          </Typography>

          {!notFound ? (
            <PremiumTable
              dataTestId="premiums-table"
              columns={effectiveDateList}
              rows={
                premiumRows?.map((item) => ({
                  manualPremium: item.manualPremium,
                  uslh: item.unitedStatesLongshoreAndHarbor,
                  totalManualPremium: item.totalManualPremium,
                  waiverOfSubrogationFactor: item.waiverOfSubFactor,
                  employersLiabilityIncreasedLimitsFactor:
                    item.employersLiabilityIncreasedLimitsFactor,
                  employersLiabilityVoluntaryCompensationFlatCharge: item.elvcFlatCharge,
                  deductibleCredit: item.deductibleCredit,
                  totalSubjectPremium: item.totalSubjectPremium,
                  experienceModification: item.expMod,
                  totalModifiedPremium: item.totalModifiedPremium,
                  scheduleRatingFactor: item.scheduleRatingFactor,
                  chargeForNonRatable: item.chargeForNonRatable,
                  balanceToMinimumPremiumStateAct: item.stateAct,
                  balanceToMinimumPremiumAdmiraltyFema: item.fela,
                  assignedRiskAdjustmentProgramSurcharge: item.arap,
                  totalStandardPremium: item.totalStandardPremium,
                  premiumDiscount: item.premiumDiscount,
                  coalMineDiseaseCharge: item.coalMineDiseaseCharge,
                  expenseConstant: item.expenseConstant,
                  terrorismCharge: item.terrorismCharge,
                  catastrophe: item.catastrophy,
                  estimatedAnnualPremium: item.estimatedAnnualPremium,
                  auditNonComplianceCharge: item.auditNonCompCharges,
                  totalAmountDue: item.totalAmountDue,
                  carrierCalculatedTotalAmountDue: item.carrierCalculateTotalDue,
                })) || []
              }
            />
          ) : (
            <EmptyContent
              sx={{ mb: 8 }}
              description="This table will be updated if the policy has any premium data to report."
            />
          )}
        </CardContent>
      </Card>

      {!notFound && (
        <Card
          sx={[
            () => ({
              display: 'flex',
              zIndex: 'unset',
              overflow: 'unset',
              mt: 5,
              borderRadius: 2,
              boxShadow: 'var(--customShadows-card)',
              width: headerWidth,
            }),
          ]}
        >
          <CardContent
            sx={{ flexGrow: 1, padding: '0px' }}
            data-testid="total-policy-premiums-card"
          >
            <Box sx={{ paddingTop: '1.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
              <Typography variant="h5" data-testid="total-policy-premium-title">
                Total policy premium
              </Typography>
              {isEditMode ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Total policy premium"
                  sx={{ mt: '1rem' }}
                  value={draftTotalPolicyPremium}
                  error={Boolean(fieldError)}
                  helperText={fieldError}
                  onChange={(event) =>
                    setDraftTotalPolicyPremium(event.target.value.replace(/\D/g, '').slice(0, 10))
                  }
                  slotProps={{
                    input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                    htmlInput: { maxLength: 10, inputMode: 'numeric' },
                  }}
                  data-testid="total-policy-premium-input"
                />
              ) : (
                <>
                  <Typography
                    sx={{ paddingTop: '1.5rem' }}
                    variant="body2"
                    color="text.secondary"
                    data-testid="total-policy-premium-sub-title"
                  >
                    Total policy premium:
                  </Typography>
                  <Typography variant="subtitle2" data-testid="total-policy-premium-sub-value">
                    {premiumRows?.map((item) => formatDollarAmount(item.totalPolicyPremium || 0))[0] ||
                      ''}
                  </Typography>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
});

export default memo(Premium);
